import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface UseAutoFitPdfHeightOptions {
  /** Debounce duration for resize/scroll events */
  debounceMs?: number;
  /** Extra space to leave at the bottom of the viewport */
  bottomMarginPx?: number;
  /** Minimum page height fallback */
  minHeight?: number;
}

export interface UseAutoFitPdfHeightReturn {
  containerRef: React.RefObject<HTMLDivElement | null>;
  pageHeight: number;
  /** Manually trigger a remeasurement (e.g., after external layout changes) */
  remeasure: () => void;
  /** Current container width in pixels, for fitting Page.width */
  containerWidth: number;
}

/**
 * Computes an appropriate `Page.height` for react-pdf so the canvas fills the
 * remaining viewport height below the container's top edge.
 *
 * This works well when the PDF viewer lives inside a page with headers/toolbars
 * above it and should grow to the bottom of the screen. It listens to window
 * resize and scroll events, with debouncing, and can also be manually
 * re-triggered via the returned `remeasure` function.
 */
export const useAutoFitPdfHeight = (
  options: UseAutoFitPdfHeightOptions = {},
): UseAutoFitPdfHeightReturn => {
  const { debounceMs = 150, bottomMarginPx = 16, minHeight = 384 } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [pageHeight, setPageHeight] = useState<number>(minHeight);
  const timerRef = useRef<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const measureNow = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const available = Math.floor(
      window.innerHeight - rect.top - bottomMarginPx,
    );
    setPageHeight(Math.max(minHeight, available));
    // Compute actual content width (clientWidth includes padding). Subtract paddings
    // to avoid the canvas being wider than the visible content area which causes
    // apparent clipping when zooming.
    const styles = window.getComputedStyle(el);
    const paddingLeft = Number.parseFloat(styles.paddingLeft || "0");
    const paddingRight = Number.parseFloat(styles.paddingRight || "0");
    const contentWidth = el.clientWidth - paddingLeft - paddingRight;
    setContainerWidth(Math.max(0, Math.floor(contentWidth)));
  }, [bottomMarginPx, minHeight]);

  const debouncedMeasure = useMemo(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        measureNow();
        timerRef.current = null;
      }, debounceMs);
    };
  }, [debounceMs, measureNow]);

  useEffect(() => {
    // Initial measure after mount
    debouncedMeasure();

    const onResize = () => debouncedMeasure();
    const onScroll = () => debouncedMeasure();

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    // Observe container size changes as an additional signal
    const ro = new ResizeObserver(() => debouncedMeasure());
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      ro.disconnect();
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [debouncedMeasure]);

  return {
    containerRef,
    pageHeight,
    remeasure: debouncedMeasure,
    containerWidth,
  };
};
